<script lang="ts">
  import ProblemNotice from '../lib/ProblemNotice.svelte'
  import { appProblem } from '../lib/problems'
  import { t } from '../lib/i18n.svelte'
  import { account, type Provider } from '../lib/account.svelte'
  import { friends, shortId } from '../lib/friends.svelte'
  import { addFriendLink, isUserId, userIdFromInput } from '../lib/friend-link'
  import { prefs } from '../lib/prefs.svelte'
  import { navigate } from '../lib/router.svelte'
  import { watchSharedRoom } from '../lib/room-navigation'
  import { tone } from '../lib/social'
  import { isTauri } from '../lib/tauri'
  import { handleProblem, normalizeHandle } from '../lib/handle'
  import DevLogin from '../lib/DevLogin.svelte'
  import FriendsRail from '../lib/FriendsRail.svelte'
  import Loading from '../lib/Loading.svelte'
  import type { Person } from '../lib/social-protocol'

  /** A friend the page was opened to add (`/add/<id>`), if any. */
  let { addId = null, addName = '' }: { addId?: string | null; addName?: string } = $props()

  const PENDING_KEY = 'sofa.pending-add'
  const inDesktopApp = isTauri()
  let input = $state('')
  let inputError = $state<string | null>(null)
  let copied = $state(false)
  let serverInput = $state(friends.url)

  const signedIn = $derived(account.signedIn)
  let deleteConfirmation = $state('')
  const myName = $derived(prefs.displayName.trim() || account.name || '')
  const myLink = $derived(
    friends.userId ? addFriendLink(friends.userId, myName, location.origin) : '',
  )

  // A friend link opened while signed out survives the sign-in round trip.
  const pendingAdd = $derived.by((): { id: string; name: string } | null => {
    if (addId && isUserId(addId)) return { id: addId, name: addName }
    try {
      const saved = sessionStorage.getItem(PENDING_KEY)
      const parsed = saved ? (JSON.parse(saved) as { id?: string; name?: string }) : null
      return parsed?.id && isUserId(parsed.id) ? { id: parsed.id, name: parsed.name ?? '' } : null
    } catch {
      return null
    }
  })
  const offerAdd = $derived(
    pendingAdd !== null &&
      pendingAdd.id !== friends.userId &&
      !friends.friends.some((f) => f.user_id === pendingAdd.id),
  )

  function clearPending() {
    try {
      sessionStorage.removeItem(PENDING_KEY)
    } catch {
      // Nothing to clear.
    }
  }

  function signIn(provider: Provider) {
    if (pendingAdd) {
      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pendingAdd))
      } catch {
        // The link can be opened again after signing in.
      }
    }
    void account.signInWith(provider)
  }

  /** What the last search found: a person, null for nobody, undefined before any search. */
  let found = $state<Person | null | undefined>(undefined)
  let searching = $state(false)
  let sent = $state<{ id: string; name: string }[]>([])

  const relation = $derived.by(() => {
    if (!found) return null
    if (found.user_id === friends.userId) return 'self'
    if (friends.friends.some((f) => f.user_id === found?.user_id)) return 'friend'
    if (friends.requests.some((r) => r.user_id === found?.user_id)) return 'incoming'
    if (sent.some((p) => p.id === found?.user_id)) return 'sent'
    return 'open'
  })

  /** One box for both ways in: an @handle is searched, a pasted link is sent at once. */
  async function add(event: SubmitEvent) {
    event.preventDefault()
    found = undefined
    const id = userIdFromInput(input)
    if (id) {
      if (id === friends.userId) {
        inputError = t('That is you.')
        return
      }
      inputError = null
      friends.request(id)
      sent = [...sent, { id, name: shortId(id) }]
      input = ''
      return
    }
    const handle = normalizeHandle(input)
    const problem = handleProblem(handle)
    if (problem) {
      inputError = t('Type their @handle, or paste the link they sent you.')
      return
    }
    inputError = null
    searching = true
    found = await friends.search(handle)
    searching = false
  }

  function ask(person: Person) {
    friends.request(person.user_id)
    sent = [...sent, { id: person.user_id, name: person.name }]
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(myLink)
      copied = true
      setTimeout(() => (copied = false), 1500)
    } catch {
      // The link is on screen either way.
    }
  }

  const linkTail = $derived(friends.userId ?? '')
  const linkHead = $derived(`${location.host}/add/`)
  const initial = (name: string) => (name[0] ?? '?').toUpperCase()
</script>

{#snippet notices()}
  {#if friends.notice}
    <ProblemNotice problem={appProblem(friends.notice, 'account')} compact />
  {/if}
  {#if account.error}
    <ProblemNotice problem={appProblem(account.error, 'account')} compact />
  {/if}
  {#if signedIn && friends.status === 'offline'}
    <div class="notice notice--warn">
      {t('Cannot reach the friends server at {url}. Is it running?', { url: friends.url })}
    </div>
  {/if}
{/snippet}

{#if account.restoring}
  <Loading />
{:else if !signedIn}
  <div class="page settings-page stack" style="gap: 20px">
    <div class="row" style="flex-wrap: nowrap">
      <button
        type="button"
        class="round-button"
        aria-label={t('Back')}
        onclick={() => navigate(inDesktopApp ? '/host' : '/')}
      >
        <svg viewBox="0 -960 960 960" aria-hidden="true"
          ><path
            d="m274-450 227 227q9 9 9 21t-9 21q-9 9-21 9t-21-9L181-459q-5-5-7-10t-2-11q0-6 2-11t7-10l278-278q9-9 21-9t21 9q9 9 9 21t-9 21L274-510h496q13 0 21.5 8.5T800-480q0 13-8.5 21.5T770-450H274Z"
          /></svg
        >
      </button>
      <h1 style="margin: 0; font-size: 2.25rem; letter-spacing: -0.03em">{t('Friends')}</h1>
    </div>
    {@render notices()}
    <div class="card stack">
      <div>
        <h2>{t('Sign in to use friends')}</h2>
        <p class="faint" style="margin: 0">
          {t(
            'Friends, invites and seeing who is online need an account. Sharing and watching a window never do.',
          )}
        </p>
      </div>
      {#if pendingAdd}
        <div class="notice">
          {t('Sign in to add {name} as a friend.', {
            name: pendingAdd.name || shortId(pendingAdd.id),
          })}
        </div>
      {/if}
      {#if account.available}
        <div class="row">
          <button
            type="button"
            class="primary"
            disabled={account.busy}
            onclick={() => signIn('google')}>{t('Continue with Google')}</button
          >
        </div>
        <DevLogin />
      {:else}
        <div class="notice notice--warn">{t('Accounts are not set up in this build.')}</div>
      {/if}
    </div>
  </div>
{:else}
  <div class="friends-split">
    <FriendsRail {sent} />

    <main class="friends-main">
      <div class="friends-panel">
        <button
          type="button"
          class="link-btn link-btn--dim friends-panel__back"
          onclick={() => navigate('/')}>← {t('Back')}</button
        >
        {@render notices()}

        {#if offerAdd && pendingAdd}
          <div class="card stack">
            <h2>
              {t('Add {name} as a friend?', { name: pendingAdd.name || shortId(pendingAdd.id) })}
            </h2>
            <p class="faint" style="margin: 0">
              {t('They will get a request. You see each other online once they accept.')}
            </p>
            <div class="row">
              <button
                type="button"
                class="primary"
                disabled={friends.status !== 'online'}
                onclick={() => {
                  friends.request(pendingAdd.id)
                  clearPending()
                  navigate('/friends')
                }}>{t('Send request')}</button
              >
              <button
                type="button"
                class="ghost"
                onclick={() => {
                  clearPending()
                  navigate('/friends')
                }}>{t('Not now')}</button
              >
            </div>
          </div>
        {/if}

        {#if friends.invites.length}
          <div class="settings-group">
            {#each friends.invites as invite (invite.at)}
              <div class="settings-row">
                <div class="settings-row__text">
                  <span>{t('{name} invited you', { name: invite.name })}</span>
                  <span class="faint mono">{invite.code}</span>
                </div>
                <div class="row">
                  <button type="button" class="primary" onclick={() => watchSharedRoom(invite)}
                    >{t('Join')}</button
                  >
                  <button
                    type="button"
                    class="ghost"
                    onclick={() => friends.dismissInvite(invite.at)}>{t('Dismiss')}</button
                  >
                </div>
              </div>
            {/each}
          </div>
        {/if}

        <div>
          <h1 class="friends-panel__title">{t('Add a friend')}</h1>
          <p class="friends-panel__sub">
            {t('Type their @handle, or paste a link they sent you.')}
          </p>
        </div>

        <form class="add-row" onsubmit={add}>
          <input
            bind:value={input}
            oninput={() => {
              found = undefined
              inputError = null
            }}
            placeholder={t('@handle or link')}
            autocomplete="off"
            autocapitalize="none"
            spellcheck="false"
            aria-label={t('Friend handle or link')}
          />
          <button
            class="primary"
            type="submit"
            disabled={input.trim() === '' || searching || friends.status !== 'online'}
            >{t('Search')}</button
          >
        </form>
        {#if inputError}
          <ProblemNotice
            compact
            problem={{
              title: t('Check your friend’s details'),
              message: inputError,
              tone: 'warning',
              retry: false,
            }}
          />
        {/if}
        {#if found === null}
          <p class="faint" style="margin: 0">{t('Nobody has that handle.')}</p>
        {:else if found}
          <div class="found-card">
            <span class="avatar avatar--lg avatar--{tone(found.name)}">{initial(found.name)}</span>
            <span style="flex: 1; min-width: 0">
              <strong style="display: block; font-size: 1rem">{found.name}</strong>
              <span class="faint">@{found.handle}</span>
            </span>
            {#if relation === 'self'}
              <span class="faint">{t('That is you.')}</span>
            {:else if relation === 'friend'}
              <span class="faint">{t('Already friends')}</span>
            {:else if relation === 'sent'}
              <span class="faint">{t('Request sent')}</span>
            {:else if relation === 'incoming'}
              <button type="button" class="btn-dark" onclick={() => friends.accept(found!.user_id)}
                >{t('Accept')}</button
              >
            {:else}
              <button type="button" class="btn-dark" onclick={() => found && ask(found)}
                >{t('Add friend')}</button
              >
            {/if}
          </div>
        {/if}

        <div class="or-rule"><span></span>{t('or send yours')}<span></span></div>

        <div class="invite-bar">
          <div class="invite-bar__url">
            <div class="eyebrow">{t('Your friend link')}</div>
            <code>{linkHead}<b>{linkTail}</b></code>
          </div>
          <button type="button" class="primary" onclick={copyLink} disabled={!myLink}>
            {copied ? t('Copied') : t('Copy link')}
          </button>
        </div>
        <p class="faint" style="margin: -14px 0 0; font-size: 13px">
          {t('It only lets them ask to be your friend.')}
        </p>

        <details class="friends-more">
          <summary>{t('Account and server')}</summary>
          <div class="stack" style="gap: 14px; padding-top: 12px">
            <div class="field">
              <label for="server">{t('Friends server')}</label>
              <div class="row" style="flex-wrap: nowrap">
                <input
                  id="server"
                  bind:value={serverInput}
                  placeholder="ws://127.0.0.1:8080/social"
                  class="mono"
                  spellcheck="false"
                />
                <button type="button" onclick={() => friends.setUrl(serverInput)}>{t('Use')}</button
                >
              </div>
            </div>
            <div class="row">
              <button type="button" class="ghost" onclick={() => account.signOut()}
                >{t('Sign out')}</button
              >
            </div>
            <details>
              <summary>{t('Delete account')}</summary>
              <p>
                {t(
                  'Permanently delete your account, friends and requests. Type DELETE to confirm.',
                )}
              </p>
              <input
                bind:value={deleteConfirmation}
                aria-label={t('Type DELETE to confirm')}
                autocomplete="off"
              />
              <button
                type="button"
                disabled={deleteConfirmation !== 'DELETE' || friends.status !== 'online'}
                onclick={() => {
                  friends.deleteAccount()
                  deleteConfirmation = ''
                }}>{t('Delete account permanently')}</button
              >
            </details>
          </div>
        </details>
      </div>
    </main>
  </div>
{/if}
